import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  HiArrowLeft, HiPencil, HiTrash,
  HiCheckCircle, HiPrinter,
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import { supabase } from '../supabase/client';
import { useAuth } from '../context/AuthContext';
import { formatDateLong } from '../utils/format';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  Draft:  'bg-gray-100  text-gray-600',
  Sent:   'bg-blue-100  text-blue-700',
  Signed: 'bg-green-100 text-green-700',
};

// ─── Delete Modal ─────────────────────────────────────────────────────────────

function DeleteModal({ contract, onClose, onConfirm, deleting }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl border border-gray-100 p-6 text-center">
        <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <HiTrash className="w-7 h-7 text-red-500" />
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-2">Delete Contract</h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          Delete <span className="font-semibold text-gray-800">{contract.title}</span>?
          <br />This action cannot be undone.
        </p>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white rounded-xl transition disabled:opacity-60"
          >
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ContractDetail() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const { id }     = useParams();

  const [contract,    setContract]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [signing,     setSigning]     = useState(false);
  const [showDelete,  setShowDelete]  = useState(false);
  const [deleting,    setDeleting]    = useState(false);

  const fetchContract = useCallback(async () => {
    const { data, error } = await supabase
      .from('contracts')
      .select('*, clients(name, email, company)')
      .eq('id', id)
      .eq('user_id', user.id)
      .single();

    if (error || !data) {
      toast.error('Contract not found');
      navigate('/contracts');
    } else {
      setContract(data);
    }
    setLoading(false);
  }, [id, user.id, navigate]);

  useEffect(() => { fetchContract(); }, [fetchContract]);

  const markAsSigned = async () => {
    setSigning(true);
    const now = new Date().toISOString();
    const { error } = await supabase
      .from('contracts')
      .update({ status: 'Signed', signed_at: now })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Contract marked as signed!');
      setContract((prev) => ({ ...prev, status: 'Signed', signed_at: now }));
    }
    setSigning(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    const { error } = await supabase
      .from('contracts')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Contract deleted');
      navigate('/contracts');
    }
    setDeleting(false);
  };

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout title="Contract">
        <div className="flex items-center justify-center py-32">
          <svg className="animate-spin h-8 w-8 text-indigo-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      </Layout>
    );
  }

  if (!contract) return null;

  const client = contract.clients ?? {};

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout title={contract.title}>

      {/* Action bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/contracts')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition"
          >
            <HiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900">{contract.title}</h2>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[contract.status]}`}>
                {contract.status}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {client.name} · Created {formatDateLong(contract.created_at)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium
              text-gray-600 hover:bg-gray-100 rounded-xl transition border border-gray-200"
          >
            <HiPrinter className="w-4 h-4" /> Print
          </button>
          <button
            onClick={() => navigate(`/contracts/${id}/edit`)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium
              text-gray-600 hover:bg-gray-100 rounded-xl transition border border-gray-200"
          >
            <HiPencil className="w-4 h-4" /> Edit
          </button>
          {contract.status !== 'Signed' && (
            <button
              onClick={markAsSigned}
              disabled={signing}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold
                bg-green-600 hover:bg-green-700 text-white rounded-xl transition disabled:opacity-60 shadow-sm"
            >
              <HiCheckCircle className="w-4 h-4" />
              {signing ? 'Updating…' : 'Mark as Signed'}
            </button>
          )}
          <button
            onClick={() => setShowDelete(true)}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition"
            title="Delete contract"
          >
            <HiTrash className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Contract document ── */}
      <div
        id="contract-print"
        className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden
          print:shadow-none print:border-none print:rounded-none"
      >
        {/* Indigo header band */}
        <div className="bg-indigo-600 px-8 py-7 flex flex-col sm:flex-row justify-between gap-5">
          <div>
            <div className="flex items-center gap-2.5 mb-1.5">
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-base">P</span>
              </div>
              <span className="text-white font-bold text-xl tracking-tight">PortalFlow</span>
            </div>
            <p className="text-indigo-300 text-xs">Client Portal &amp; Contract Manager</p>
          </div>
          <div className="text-right">
            <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1">Contract</p>
            <p className="text-white font-bold text-2xl tracking-tight leading-tight max-w-xs text-right">
              {contract.title}
            </p>
          </div>
        </div>

        {/* Document body */}
        <div className="px-8 py-8">

          {/* Parties + dates */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8 pb-8 border-b border-gray-100">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Client</p>
              <p className="text-sm font-bold text-gray-900">{client.name}</p>
              {client.company && <p className="text-sm text-gray-500 mt-0.5">{client.company}</p>}
              {client.email   && <p className="text-sm text-gray-500 mt-0.5">{client.email}</p>}
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Created</p>
              <p className="text-sm font-medium text-gray-900">{formatDateLong(contract.created_at)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Status</p>
              <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[contract.status]}`}>
                {contract.status}
              </span>
              {contract.signed_at && (
                <p className="text-xs text-gray-400 mt-1.5">
                  Signed {formatDateLong(contract.signed_at)}
                </p>
              )}
            </div>
          </div>

          {/* Contract body text */}
          <div className="prose prose-sm max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed bg-transparent border-0 p-0 m-0">
              {contract.content}
            </pre>
          </div>

          {/* Signature block */}
          <div className="mt-12 pt-8 border-t border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                  Service Provider Signature
                </p>
                <div className="h-12 border-b-2 border-gray-300 mb-2" />
                <p className="text-xs text-gray-400">Signature &amp; Date</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                  Client Signature
                </p>
                {contract.status === 'Signed' ? (
                  <div className="h-12 flex items-end pb-2 border-b-2 border-green-400">
                    <span className="text-green-600 font-semibold text-sm italic">{client.name}</span>
                    <span className="text-green-500 text-xs ml-3">{formatDateLong(contract.signed_at)}</span>
                  </div>
                ) : (
                  <div className="h-12 border-b-2 border-gray-300 mb-2" />
                )}
                <p className="text-xs text-gray-400 mt-2">
                  {contract.status === 'Signed' ? 'Electronically signed' : 'Signature & Date'}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Generated by <span className="font-semibold text-gray-500">PortalFlow</span> · Professional Contract Management
            </p>
          </div>
        </div>
      </div>

      {showDelete && (
        <DeleteModal
          contract={contract}
          onClose={() => setShowDelete(false)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
    </Layout>
  );
}
